import { useEffect, useRef, type MutableRefObject } from 'react';

import { useUIStore } from '@/stores';
import type { User } from '@/types';

import { CURRENT_TOUR_VERSION } from '../steps/tour-steps';
import { getReminderCount, isDismissed } from '../utils/dismiss-flag';
import { clearProgress, readProgress } from '../utils/tour-progress';
import { silentlyMarkSeen } from './tour-persistence';

/**
 * Maximum number of "Remind me later" clicks per tour version before the
 * affordance is hidden. After the limit is reached the user must use Skip
 * (which sets seen_version) or complete the tour. The auto-start guard
 * also starts treating those users as silently opted-out.
 */
export const REMIND_LATER_LIMIT = 3;

/**
 * Routes the auto-start effect is willing to fire on. If the user opened a
 * deep link (e.g. `/trades/abc-123`) we keep them where they were rather
 * than hijacking the navigation to `/inventory`. The Sidebar "Take a tour"
 * button remains the manual escape hatch — manual `start()` still
 * navigates to `/inventory` because that is where the tour content lives.
 *
 * `'/'` is included because `router.tsx`'s indexRoute redirects to
 * `/inventory`; depending on timing the auto-start effect may run while
 * the pathname is still the literal `/`.
 */
const DEFAULT_TOUR_ROUTES: ReadonlyArray<string> = ['/', '/inventory'];

const isDefaultLandingRoute = (pathname: string): boolean =>
  DEFAULT_TOUR_ROUTES.includes(pathname);

interface ResumeProgressArgs {
  user: User | null;
  isActive: boolean;
  visibleStepsLength: number;
  setStepIndex: (n: number) => void;
  setFrameIndex: (n: number) => void;
  setIsActive: (v: boolean) => void;
  sidebarCollapsedAtStartRef: MutableRefObject<boolean | null>;
  triggeredThisSessionRef: MutableRefObject<boolean>;
}

/**
 * Resume-on-mount: if a previous render in this tab persisted progress,
 * restore the tour at that position before the auto-start branch runs.
 * sessionStorage scope means only this tab resumes — a fresh tab starts
 * over. Stale / out-of-bounds progress is dropped so auto-start can fire
 * cleanly from step 0.
 *
 * Behaviour kept byte-for-byte identical to the prior inline effect so
 * existing tests (TourProvider.test, TourProvider.frames.test) pass without
 * modification — only the file location moved.
 */
export const useResumeProgress = (args: ResumeProgressArgs): void => {
  const {
    user,
    isActive,
    visibleStepsLength,
    setStepIndex,
    setFrameIndex,
    setIsActive,
    sidebarCollapsedAtStartRef,
    triggeredThisSessionRef,
  } = args;
  const resumeAttemptedRef = useRef(false);

  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    if (!user) return;
    // Resume should only race the auto-start branch; if the tour was
    // already started in this React lifecycle (rare — same tab without a
    // refresh), there's nothing to resume.
    if (isActive) return;
    if (user.onboardingSeenVersion >= CURRENT_TOUR_VERSION) {
      // User already finished this tour version — drop any stale progress
      // and let the silent path handle them.
      clearProgress();
      resumeAttemptedRef.current = true;
      return;
    }
    const progress = readProgress();
    if (!progress) {
      resumeAttemptedRef.current = true;
      return;
    }
    // Version mismatch: a deploy bumped CURRENT_TOUR_VERSION between the
    // saved-progress moment and now. Drop the stale payload.
    if (progress.version !== CURRENT_TOUR_VERSION) {
      clearProgress();
      resumeAttemptedRef.current = true;
      return;
    }
    // Out-of-bounds: visible-steps shape may have changed (permission
    // change between sessions). Drop and let auto-start fire from step 0.
    if (
      progress.stepIndex < 0 ||
      progress.stepIndex >= visibleStepsLength
    ) {
      clearProgress();
      resumeAttemptedRef.current = true;
      return;
    }
    triggeredThisSessionRef.current = true;
    // Sidebar capture: we can't know the user's pre-tour preference
    // post-refresh; capture whatever it is now so stop() restores it.
    if (sidebarCollapsedAtStartRef.current === null) {
      sidebarCollapsedAtStartRef.current = useUIStore.getState().sidebarCollapsed;
    }
    setStepIndex(progress.stepIndex);
    setFrameIndex(progress.frameIndex);
    setIsActive(true);
    resumeAttemptedRef.current = true;
  }, [user, isActive, visibleStepsLength]); // eslint-disable-line react-hooks/exhaustive-deps
};

interface AutoStartArgs {
  user: User | null;
  isActive: boolean;
  start: () => Promise<void> | void;
  setUser: (u: User) => void;
  triggeredThisSessionRef: MutableRefObject<boolean>;
}

/**
 * Auto-start the tour for fresh users (seen_version === 0) on the default
 * landing route. Skipped for:
 *   - users who already saw the current version,
 *   - users on a deep-link route (`/trades/abc` etc.),
 *   - users who hit the `Remind me later` limit,
 *   - users with the dismissed flag set (background-retries the PATCH).
 *
 * Behaviour kept byte-for-byte identical to the prior inline effect.
 */
export const useAutoStart = (args: AutoStartArgs): void => {
  const { user, isActive, start, setUser, triggeredThisSessionRef } = args;

  useEffect(() => {
    if (!user) return;
    if (user.onboardingSeenVersion >= CURRENT_TOUR_VERSION) return;
    if (triggeredThisSessionRef.current) return;
    if (isActive) return;
    // Updated rule: only the seen_version === 0 branch auto-fires the full
    // tour. For 0 < seen_version < CURRENT_TOUR_VERSION the WhatsNewBanner
    // does the asking — no auto-start. Returning here (without flagging
    // triggeredThisSessionRef) is intentional so the banner remains the
    // single source of truth for the upgrade flow.
    if (user.onboardingSeenVersion > 0) {
      return;
    }
    // Deep-link guard: if the user opened a non-default route (e.g.
    // `/trades/123`), do NOT auto-fire. Hijacking the navigation to
    // `/inventory` discards the URL the user expected to land on.
    if (!isDefaultLandingRoute(window.location.pathname)) {
      return;
    }
    // Reminder limit: if the user has clicked "Remind me later"
    // REMIND_LATER_LIMIT times for this version AND has not explicitly
    // dismissed the tour, treat them as silently opted-out.
    if (
      !isDismissed(CURRENT_TOUR_VERSION) &&
      getReminderCount(CURRENT_TOUR_VERSION) >= REMIND_LATER_LIMIT
    ) {
      triggeredThisSessionRef.current = true;
      return;
    }
    // If the user dismissed the tour in a previous session but the PATCH
    // never succeeded (server still says 0), skip the auto-start and
    // re-attempt the PATCH in the background.
    if (isDismissed(CURRENT_TOUR_VERSION)) {
      triggeredThisSessionRef.current = true;
      void silentlyMarkSeen(user, setUser);
      return;
    }
    const t = setTimeout(() => {
      void start();
    }, 300);
    return () => clearTimeout(t);
  }, [user, isActive, start, setUser]); // eslint-disable-line react-hooks/exhaustive-deps
};
